import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { DialogUtils } from '@shared/ui-react/Dialog';
import { Button } from '@shared/ui-react/Button';
import { Ajax2 } from '@shared/Ajax';
import { Notify } from '@shared/ui-native/Notify';
import { DocumentEditForm } from '@shared/documents/ui/DocumentEditForm';
import { useCtrlASelect } from '@shared/ui-react/ctrl-a-select-handler';
import { MapWrapper } from '@openlayers/MapWrapper';
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { cs } from '@shared/utils/utils-classname';

export const ExternalDocumentCreateEditDialog: React.FC<{
    treeData: ITreeData;
    document?: Partial<IDocument>;
    uuid?: string;
    onSuccess?: (data: any) => void;
    onClose?: () => void;
    mapWrapper?: MapWrapper;
    mapTools: MapToolsStore;
    activateEditLocation: boolean;
}> = ({ treeData, document, onSuccess, onClose, mapWrapper, mapTools, uuid, activateEditLocation }) => {
    const form = useForm<IDocument & { latLon: [number, number] }>({
        mode: 'all',
        reValidateMode: 'onChange',
        values: (document as any) ?? null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [id, setId] = useState(null);

    useEffect(() => {
        if (!uuid) return;

        setIsLoading(true);
        Ajax2({
            url: kuura.getContextPath() + `/v1/document/external/${uuid}`,
        })
            .then(resp => {
                setIsLoading(false);
                if (resp.document.geographicPoint) {
                    resp.document.latLon = [resp.document.geographicPoint.lat, resp.document.geographicPoint.lon];
                }
                setId(resp.document.id);
                form.reset(resp.document);
            })
            .catch(Notify.error);
    }, [uuid]);

    const onSubmit = data => {
        const ajaxData = {
            projectId: kuura.getProjectId(),
            ...data,
        };
        const lat = data.latLon ? data.latLon[0] : null;
        const lon = data.latLon ? data.latLon[1] : null;
        if (lat || lon) {
            ajaxData.geographicPoint = { lat, lon, elevation: 0 };
        }

        setIsLoading(true);
        const request = uuid
            ? Ajax2({
                  url: kuura.getContextPath() + '/documents_bulk_edit.json',
                  method: 'POST',
                  contentType: 'urlencoded',
                  data: {
                      externalIds: [id],
                      ...ajaxData,
                  },
              })
            : Ajax2({
                  url: kuura.getContextPath() + '/v1/document/external',
                  method: 'POST',
                  contentType: 'json',
                  data: ajaxData,
              });

        request
            .then(resp => {
                if (!resp.status) {
                    throw resp;
                }
                Notify.success();
                DialogUtils.close();
                onSuccess(data);
            })
            .finally(() => setIsLoading(false))
            .catch(Notify.error);

        return request;
    };

    useCtrlASelect('external-document-create-dialog');

    return (
        <div id='external-document-create-dialog' className={cs({ 'overlay-loading': isLoading })}>
            <main className='flex-column gap-half' style={{ width: '100%' }}>
                <FormProvider {...form}>
                    <DocumentEditForm hasUrl={true} treeData={treeData} mapWrapper={mapWrapper} mapTools={mapTools} activateEditLocation={activateEditLocation} />
                </FormProvider>
            </main>

            <footer style={{ marginTop: 20 }}>
                <hr />
                <Button className='ik-button-secondary--subtle' onClick={onClose}>
                    {__('general.close')}
                </Button>
                <Button className='ik-button-primary right' disabled={!form.formState.isDirty || !form.formState.isValid || isLoading} onClick={form.handleSubmit(onSubmit)}>
                    {__(uuid ? 'general.save' : 'general.create')}
                </Button>
            </footer>
        </div>
    );
};
